import type {
  ImportModule,
  ImportResult,
  ReportExportRequest,
} from '@internal/orquestra-contract';
import { OrquestraRequestError } from './OrquestraApi';
import { OrquestraEntityAttachmentsClient } from './OrquestraEntityAttachmentsClient';

/**
 * Erro das transferências de arquivo com a mensagem do backend (`{ error }`)
 * em `serverMessage`, para o toast mostrar o motivo real (origem `detail`).
 */
async function fileTransferError(
  response: Response,
  message: string,
): Promise<OrquestraRequestError> {
  const error = new OrquestraRequestError(message, response.status);
  try {
    const body = (await response.json()) as { error?: string } | null;
    if (body?.error) error.serverMessage = body.error;
  } catch {
    // corpo sem JSON (ex.: erro de proxy) — fica só a mensagem padrão
  }
  return error;
}

/**
 * Estende `OrquestraEntityAttachmentsClient` com as transferências de arquivo (Fase 5): export de
 * relatório e import por planilha — leitura de `Blob` e upload `multipart`, fora do
 * helper JSON. Classe intermediária da cadeia de clients.
 */
export class OrquestraReportClient extends OrquestraEntityAttachmentsClient {
  /**
   * Exporta o relatório do projeto. Lê o arquivo como `Blob` e resolve o nome a
   * partir do `Content-Disposition`; o disparo do download fica na UI.
   */
  async exportReport(
    projectId: number,
    request: ReportExportRequest,
  ): Promise<{ blob: Blob; fileName: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${projectId}/report-export`,
      this.jsonInit('POST', request),
    );
    if (!response.ok) {
      throw await fileTransferError(
        response,
        `Falha ao exportar o relatório (${response.status})`,
      );
    }
    const match = /filename="([^"]+)"/.exec(
      response.headers.get('Content-Disposition') ?? '',
    );
    return {
      blob: await response.blob(),
      fileName: match?.[1] ?? `relatorio.${request.format}`,
    };
  }

  /** Baixa o template de import de um módulo (blob + nome do arquivo). */
  async downloadImportTemplate(
    projectId: number,
    module: ImportModule,
  ): Promise<{ blob: Blob; fileName: string }> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${projectId}/${module}/import/template`,
    );
    if (!response.ok) {
      throw await fileTransferError(
        response,
        `Falha ao baixar o template (${response.status})`,
      );
    }
    const match = /filename="([^"]+)"/.exec(
      response.headers.get('Content-Disposition') ?? '',
    );
    return {
      blob: await response.blob(),
      fileName: match?.[1] ?? `${module}.xlsx`,
    };
  }

  /** Envia a planilha de import de um módulo e devolve o resultado. */
  async importSpreadsheet(
    projectId: number,
    module: ImportModule,
    file: File,
  ): Promise<ImportResult> {
    const baseUrl = await this.getBaseUrl();
    const form = new FormData();
    form.append('file', file);
    const response = await this.fetchApi.fetch(
      `${baseUrl}/projects/${projectId}/${module}/import`,
      { method: 'POST', body: form },
    );
    if (!response.ok) {
      throw await fileTransferError(
        response,
        `Falha ao importar (${response.status})`,
      );
    }
    return response.json() as Promise<ImportResult>;
  }
}
